'use client';

import * as React from 'react';

import { Input } from '../shadcn/input';
import { DateField, type DateFieldProps } from './date-field';

/**
 * `DateTimeField` — the canonical date+time picker, replacing native
 * `<input type="datetime-local">`. Composes {@link DateField} (calendar popover)
 * with a `type="time"` input and emits a single `YYYY-MM-DDTHH:mm` string —
 * the same shape a native `datetime-local` produces, so callers keep their
 * existing wire format.
 *
 * Timezone-safe: the value is a plain local wall-clock string; we never build a
 * `Date` from it or call `.toISOString()` (Critical Rule 19). Empty until BOTH
 * a date and a time are set.
 */
export interface DateTimeFieldProps extends Omit<
  DateFieldProps,
  'value' | 'onChange' | 'granularity'
> {
  /** `YYYY-MM-DDTHH:mm` or ''. */
  value: string;
  /** Emits `YYYY-MM-DDTHH:mm`, or '' when the date is cleared. */
  onChange: (value: string) => void;
  /** Step for the time input, in minutes. Default 1 (native default is 60). */
  minuteStep?: number;
  /** Accessible label for the time input. */
  timeAriaLabel?: string;
}

function splitDateTime(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const [date = '', time = ''] = value.split('T');
  // Native datetime-local can carry seconds (HH:mm:ss); keep only HH:mm.
  return { date, time: time.slice(0, 5) };
}

export function DateTimeField({
  value,
  onChange,
  minuteStep = 1,
  disabled,
  id,
  name,
  timeAriaLabel,
  ...dateFieldProps
}: DateTimeFieldProps) {
  const { date, time } = splitDateTime(value);

  const emit = (nextDate: string, nextTime: string) => {
    if (!nextDate) {
      onChange('');
      return;
    }
    // Default the time to midnight so picking only a date still yields a value.
    onChange(`${nextDate}T${nextTime || '00:00'}`);
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <div className="flex-1">
        <DateField
          {...dateFieldProps}
          id={id}
          value={date}
          onChange={(d) => emit(d, time)}
          disabled={disabled}
        />
      </div>
      <Input
        type="time"
        aria-label={timeAriaLabel}
        step={minuteStep * 60}
        className="h-11 sm:w-[8rem]"
        value={time}
        disabled={disabled || !date}
        onChange={(e) => emit(date, e.target.value)}
      />
      {name ? <input type="hidden" name={name} value={value} /> : null}
    </div>
  );
}
